import React, { useEffect, useState } from 'react';
import { tasksApi } from '../../../Tasks/tasksApi';
import { StudentDetailDrawer, Learner } from './MyLearnersPage';

/**
 * A student, as any task/queue screen already knows them. Enough to open the
 * shared detail drawer, which fetches the rest (RM, tasks, courses, tags).
 */
export interface StudentRef {
  studentExternalId: string;
  name: string;
  phone?: string | null;
  email?: string | null;
}

// Allowed tag vocabulary is the same everywhere and rarely changes — fetch once.
let allowedTagsCache: string[] | null = null;

/**
 * Drop-in wrapper around My Students' StudentDetailDrawer for use on any screen
 * that only has a StudentRef (task queues, task detail, dashboard). It seeds the
 * drawer instantly from the ref, then enriches it from the student's mapping row
 * (tags, locality, deep-link ids), and wires up tag editing + "open full
 * profile" so callers just pass a student and an onClose.
 */
const StudentDrawer: React.FC<{ student: StudentRef | null; onClose: () => void }> = ({
  student,
  onClose,
}) => {
  const [learner, setLearner] = useState<Learner | null>(null);
  const [allowedTags, setAllowedTags] = useState<string[]>(allowedTagsCache ?? []);

  useEffect(() => {
    if (allowedTagsCache) return;
    tasksApi<{ tags: string[] }>('/v1/agent-student-maps/tags/allowed')
      .then((r) => {
        allowedTagsCache = r.tags;
        setAllowedTags(r.tags);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!student) {
      setLearner(null);
      return;
    }
    // Seed from the ref so the drawer opens immediately, no spinner on the shell.
    const base = {
      studentExternalId: student.studentExternalId,
      name: student.name,
      phoneE164: student.phone ?? null,
      email: student.email ?? null,
      countryTzone: null,
      timezone: null,
      metadata: {},
      tags: [],
      band: null,
      score: null,
      breakdown: null,
    } as unknown as Learner;
    setLearner(base);

    // Enrich from the mapping row (tags, locality, deep-link ids) when present.
    let cancelled = false;
    tasksApi<{ mappings: any[] }>(
      `/v1/agent-student-maps?studentExternalId=${encodeURIComponent(
        student.studentExternalId,
      )}&take=1`,
    )
      .then((r) => {
        const row = r.mappings?.[0];
        if (cancelled || !row) return;
        setLearner({
          ...base,
          phoneE164: row.phone ?? base.phoneE164,
          email: row.email ?? base.email,
          countryTzone: row.countryTzone ?? null,
          timezone: row.timezone ?? null,
          metadata: row.metadata ?? {},
          tags: row.tags ?? [],
        } as unknown as Learner);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [student]);

  const onTagsSaved = (_id: string, tags: string[]): void => {
    setLearner((prev) => (prev ? ({ ...prev, tags } as Learner) : prev));
  };

  /**
   * Resolve the CMS deep-link ids (metadata first, else by phone/name) and
   * open the profile in a new tab — the agent's Act/task page underneath
   * stays exactly where they left it.
   */
  /** The CMS resolves a userId to the right profile and redirects; we do
   *  not need the credentialId, and searching by name or phone to find it
   *  only added ways to miss when those have drifted. */
  const openFull = (l: Learner): void => {
    window.open(
      `${process.env.PUBLIC_URL}/profile/${l.studentExternalId}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  return (
    <StudentDetailDrawer
      learner={learner}
      allowedTags={allowedTags}
      onClose={onClose}
      onTagsSaved={onTagsSaved}
      onOpenFull={openFull}
      resolving={false}
    />
  );
};

export default StudentDrawer;
